import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useFetch } from './FetchApi';
import { useNavigate } from 'react-router-dom';
import { getJwtFromCookie, isJwtExpiringSoon } from '../utils/jwt';

const AuthContext = createContext();

/**
 * Authentication context provider
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  // Initialize state from localStorage
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const stored = localStorage.getItem('auth_isLoggedIn');
    return stored ? JSON.parse(stored) : false;
  });
  const [recId, setRecid] = useState(() => {
    const stored = localStorage.getItem('auth_recId');
    return stored ? JSON.parse(stored) : null;
  });
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('auth_email') || null;
  });
  const [userRole, setUserRole] = useState(() => {
    const role = localStorage.getItem('auth_userRole') || null;
    console.log('AuthProvider: Initial userRole from localStorage:', role);
    return role;
  });
  const [requiresPasswordSetup, setRequiresPasswordSetup] = useState(() => {
    const stored = localStorage.getItem('auth_requiresPasswordSetup');
    return stored ? JSON.parse(stored) : false;
  });

  // Debug state
  const [authDebugInfo, setAuthDebugInfo] = useState(null);
  
  // State for manual status check trigger
  // Always check on initial load to verify authentication status
  const [shouldCheckStatus, setShouldCheckStatus] = useState(true);
  const hasCheckedInitialStatus = useRef(false);
  const statusRequestInFlightRef = useRef(false);
  
  // Only fetch status when shouldCheckStatus is true
  const [{data: checkLogState, isLoading: isLogLoading, error: authError}, updateStatusCheck] = useFetch(null, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    origin: ["http://127.0.0.1:8443", "http://127.0.0.1:3000", "http://localhost:3000", "http://localhost:8443", "https://localhost:3000", "https://localhost:8443"],
  }, false, true); // Don't auto-fetch, don't show error alerts
  
  // Trigger status check when shouldCheckStatus is true
  useEffect(() => {
    if (shouldCheckStatus && updateStatusCheck && !statusRequestInFlightRef.current) {
      statusRequestInFlightRef.current = true;
      updateStatusCheck('/sign/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
    }
  }, [shouldCheckStatus, updateStatusCheck]);
  
  // Check if we should verify status periodically (only after initial check)
  useEffect(() => {
    if (!isLogLoading && statusRequestInFlightRef.current) {
      statusRequestInFlightRef.current = false;
    }
    if (!hasCheckedInitialStatus.current && !isLogLoading && checkLogState !== undefined) {
      hasCheckedInitialStatus.current = true;
      // After initial check, set up periodic checks based on JWT expiration
      const jwtToken = getJwtFromCookie();
      if (jwtToken && !isJwtExpiringSoon(jwtToken, 7)) {
        // JWT is valid and not expiring soon, don't check again until it's close to expiration
        setShouldCheckStatus(false);
      }
    }
  }, [isLogLoading, checkLogState]);

  // Track previous auth state to prevent unnecessary updates
  const prevAuthState = useRef({ isLoggedIn, recId, email, userRole, requiresPasswordSetup });

  /**
   * Manually check authentication status
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      setShouldCheckStatus(true);
      // Trigger the fetch
      if (updateStatusCheck) {
        updateStatusCheck('/sign/status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  }, [updateStatusCheck]);

  /**
   * Log out the current user
   */
  const logout = useCallback(() => {
    if (isLoggedIn) {
      // Update the ref directly to prevent re-renders
      prevAuthState.current = {
        isLoggedIn: false,
        recId: null,
        email: null,
        userRole: null,
        requiresPasswordSetup: false
      };

      // Update state
      setIsLoggedIn(false);
      setRecid(null);
      setEmail(null);
      setUserRole(null);
      setRequiresPasswordSetup(false);

      // Clear localStorage
      localStorage.removeItem('auth_isLoggedIn');
      localStorage.removeItem('auth_recId');
      localStorage.removeItem('auth_email');
      localStorage.removeItem('auth_userRole');
      localStorage.removeItem('auth_requiresPasswordSetup');

      // Check status after logout
      setTimeout(() => {
        checkAuthStatus();
      }, 100);

      // Navigate after state updates
      setTimeout(() => {
        navigate("/");
      }, 0);
    }
  }, [isLoggedIn, navigate, checkAuthStatus]);

  // We're disabling the periodic cookie check as it's causing issues
  // The authentication will be handled by the /sign/status endpoint
  // which is called when the app loads

  useEffect(() => {
    // Only update if loading is complete and we have a result
    if (!isLogLoading) {
      console.log('Auth check response:', checkLogState);
      console.log('Auth error:', authError);
      // Store the full auth response for debugging
      setAuthDebugInfo(checkLogState);

      // Log current authentication state
      console.log('Current auth state:', { isLoggedIn, recId, email, userRole });

      if (!authError && checkLogState?.authenticated) {
        // User is authenticated - update state only if changed
        const newState = {
          isLoggedIn: true,
          recId: checkLogState.id,
          email: checkLogState.email,
          userRole: checkLogState.role || 'USER',
          requiresPasswordSetup: checkLogState.requiresPasswordSetup || false
        };

        // Only update state if something changed
        if (prevAuthState.current.isLoggedIn !== newState.isLoggedIn) {
          setIsLoggedIn(newState.isLoggedIn);
          localStorage.setItem('auth_isLoggedIn', JSON.stringify(newState.isLoggedIn));
        }
        if (prevAuthState.current.recId !== newState.recId) {
          setRecid(newState.recId);
          localStorage.setItem('auth_recId', JSON.stringify(newState.recId));
        }
        if (prevAuthState.current.email !== newState.email) {
          setEmail(newState.email);
          localStorage.setItem('auth_email', newState.email || '');
        }
        if (prevAuthState.current.userRole !== newState.userRole) {
          setUserRole(newState.userRole);
          localStorage.setItem('auth_userRole', newState.userRole || '');
        }
        if (prevAuthState.current.requiresPasswordSetup !== newState.requiresPasswordSetup) {
          setRequiresPasswordSetup(newState.requiresPasswordSetup);
          localStorage.setItem('auth_requiresPasswordSetup', JSON.stringify(newState.requiresPasswordSetup));
        }

        // Update ref with current state
        prevAuthState.current = newState;
      } else if (!checkLogState?.authenticated || authError) {
        // User is not authenticated - reset state
        const newState = {
          isLoggedIn: false,
          recId: null,
          email: null,
          userRole: null,
          requiresPasswordSetup: false
        };

        // Only update state if something changed
        if (prevAuthState.current.isLoggedIn !== newState.isLoggedIn) {
          setIsLoggedIn(newState.isLoggedIn);
          localStorage.removeItem('auth_isLoggedIn');
        }
        if (prevAuthState.current.recId !== newState.recId) {
          setRecid(newState.recId);
          localStorage.removeItem('auth_recId');
        }
        if (prevAuthState.current.email !== newState.email) {
          setEmail(newState.email);
          localStorage.removeItem('auth_email');
        }
        if (prevAuthState.current.userRole !== newState.userRole) {
          setUserRole(newState.userRole);
          localStorage.removeItem('auth_userRole');
        }
        if (prevAuthState.current.requiresPasswordSetup !== newState.requiresPasswordSetup) {
          setRequiresPasswordSetup(newState.requiresPasswordSetup);
          localStorage.removeItem('auth_requiresPasswordSetup');
        }

        // Update ref with current state
        prevAuthState.current = newState;
      }
    }
  }, [authError, checkLogState, email, isLogLoading, isLoggedIn, recId, userRole]);
  
  // Periodically check if JWT is expiring soon (every 5 minutes)
  // Only after initial check is complete
  useEffect(() => {
    if (!hasCheckedInitialStatus.current) {
      return; // Don't set up periodic checks until initial check is done
    }
    
    const interval = setInterval(() => {
      const jwtToken = getJwtFromCookie();
      if (!jwtToken || isJwtExpiringSoon(jwtToken, 7)) {
        setShouldCheckStatus(true);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  /**
   * Log in a user
   * @param {string|number} id - User ID
   * @param {string} userEmail - User email
   * @param {string} role - User role (optional, defaults to 'USER')
   */
  const login = useCallback((id, userEmail, role = 'USER', requiresPasswordSetup = false) => {
    // Update the ref directly to prevent re-renders
    prevAuthState.current = {
      isLoggedIn: true,
      recId: id,
      email: userEmail,
      userRole: role,
      requiresPasswordSetup: requiresPasswordSetup
    };

    // Update state only if needed
    if (!isLoggedIn) {
      setIsLoggedIn(true);
      localStorage.setItem('auth_isLoggedIn', JSON.stringify(true));
    }
    if (recId !== id) {
      setRecid(id);
      localStorage.setItem('auth_recId', JSON.stringify(id));
    }
    if (email !== userEmail) {
      setEmail(userEmail);
      localStorage.setItem('auth_email', userEmail || '');
    }
    // Set userRole from parameter
    if (userRole !== role) {
      setUserRole(role);
      localStorage.setItem('auth_userRole', role);
    }
    if (requiresPasswordSetup !== undefined) {
      setRequiresPasswordSetup(requiresPasswordSetup);
      localStorage.setItem('auth_requiresPasswordSetup', JSON.stringify(requiresPasswordSetup));
    }
    
    // Check status after login
    setTimeout(() => {
      checkAuthStatus();
    }, 100);
  }, [isLoggedIn, recId, email, userRole, checkAuthStatus]);

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      recId,
      email,
      userRole,
      requiresPasswordSetup,
      login,
      logout,
      checkAuthStatus, // Expose checkAuthStatus for manual calls
      authDebugInfo // Add debug info to the context
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

